import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, RotateCcw, CheckCircle2 } from 'lucide-react';
import { clsx } from 'clsx';
import { MathUtils } from '../math/MathUtils';
import ResponseAreaRenderer from './ResponseAreaRenderer';
import { useAuthStore } from '../../stores/authStore';

const getAssessmentLabel = (assessment) => {
  if (!assessment?.enabled) return '';
  if (assessment.category === 'informal') {
    return assessment.informalType === 'self-practice'
      ? 'Informal • Self Practice'
      : 'Informal • Class Practice';
  }
  return 'Formal Assessment';
};

const AssessmentDocPreviewModal = ({ isOpen, onClose, document: doc }) => {
  const { user } = useAuthStore();
  const pageContainerRef = useRef(null);
  const [responses, setResponses] = useState({});
  const [portalTargets, setPortalTargets] = useState([]);
  const [showModelAnswers, setShowModelAnswers] = useState(false);

  const assessment = doc?.assessment;
  const pages = doc?.pages || [];
  const assessmentLabel = getAssessmentLabel(assessment);
  const allQuestionsAnswered = useMemo(() => {
    const order = assessment?.responseAreaOrder || [];
    const configs = assessment?.responseAreas || {};
    if (order.length === 0) return false;
    return order.every((blockId) => {
      const config = configs[blockId];
      const value = responses[blockId]?.value;
      if (config?.type === 'multi-select') return Array.isArray(value) && value.length > 0;
      if (config?.type === 'image-upload') return !!value;
      return String(value ?? '').trim().length > 0;
    });
  }, [assessment?.responseAreaOrder, assessment?.responseAreas, responses]);

  const pageNodes = useMemo(
    () => pages.map((pageHtml, index) => (
      <div
        key={`preview-page-${index}`}
        className="bg-white shadow-lg rounded border border-gray-200"
        style={{
          width: '210mm',
          minHeight: '297mm',
          padding: '25.4mm',
          fontSize: '14px',
          lineHeight: '1.6',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
        dangerouslySetInnerHTML={{ __html: pageHtml || '' }}
      />
    )),
    [pages]
  );

  // Discover portal targets. The modal DOM is always mounted (hidden via CSS),
  // so by the time isOpen flips to true the dangerouslySetInnerHTML pages have
  // already been committed to the DOM tree from an earlier render.
  useEffect(() => {
    if (!isOpen) {
      setPortalTargets([]);
      return;
    }

    // Give React one extra frame to flush the committed DOM to the screen
    const frameId = requestAnimationFrame(() => {
      const container = pageContainerRef.current;
      if (!container) return;

      MathUtils.renderMath(container);

      const blocks = container.querySelectorAll('.response-area-block');
      const targets = [];
      blocks.forEach((el) => {
        const blockId = el.getAttribute('data-response-id');
        if (blockId) {
          el.removeAttribute('contenteditable');
          el.innerHTML = '';
          el.className = clsx('response-area-block', 'response-area-active');
          targets.push({ blockId, element: el });
        }
      });
      setPortalTargets(targets);
    });

    setResponses({});
    setShowModelAnswers(false);
    return () => cancelAnimationFrame(frameId);
  }, [isOpen, pages]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResponseChange = useCallback((blockId, value) => {
    setResponses((prev) => ({ ...prev, [blockId]: { value } }));
  }, []);

  const handleReset = useCallback(() => {
    setResponses({});
  }, []);

  // Always mount the DOM — hide with CSS when closed.
  // This avoids the conditional-return-null timing issue entirely.
  return (
    <div
      className={clsx('fixed inset-0 z-50 bg-gray-100 flex flex-col', !isOpen && 'hidden')}
    >
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <span className="px-3 py-1 text-xs font-bold rounded-full bg-amber-100 text-amber-800 uppercase tracking-wide">
            Preview Mode
          </span>
          <div>
            <div className="text-sm font-medium text-gray-700">
              {doc?.name || 'Untitled Document'}
            </div>
            {assessmentLabel && (
              <div className="text-xs text-gray-500">{assessmentLabel}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {assessment?.showModelAnswers && (
            <button
              type="button"
              onClick={() => setShowModelAnswers((prev) => !prev)}
              disabled={!allQuestionsAnswered}
              title={allQuestionsAnswered ? 'Show model answers' : 'Answer every response block to unlock model answers'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
            >
              <CheckCircle2 className="w-4 h-4" />
              {showModelAnswers ? 'Hide Model Answers' : 'Show Model Answers'}
            </button>
          )}
          <button
            onClick={handleReset}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-200"
          >
            <RotateCcw className="w-4 h-4" />
            Reset Answers
          </button>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
            title="Close preview"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Document pages */}
      <div className="flex-1 overflow-y-auto py-10">
        <div
          ref={pageContainerRef}
          className="mx-auto space-y-6"
          style={{ maxWidth: '210mm' }}
        >
          {pageNodes}
        </div>
      </div>

      {/* Footer info */}
      <div className="bg-amber-50 border-t border-amber-200 px-6 py-2 text-center shrink-0">
        <p className="text-xs text-amber-700">
          This is a preview. No submissions are recorded.
        </p>
      </div>

      {/* Portals for response areas */}
      {portalTargets.map(({ blockId, element }) => {
        const config = assessment?.responseAreas?.[blockId];
        if (!config) return null;
        return createPortal(
          <ResponseAreaRenderer
            key={blockId}
            blockId={blockId}
            config={config}
            value={responses[blockId]?.value ?? ''}
            onChange={(val) => handleResponseChange(blockId, val)}
            disabled={false}
            userId={user?.uid}
            showModelAnswer={showModelAnswers && assessment?.showModelAnswers}
          />,
          element
        );
      })}
    </div>
  );
};

export default AssessmentDocPreviewModal;
